/* No return/exchange badge on every PLP card, whatever collection is listed. The Bloomreach renderer only
   shows it when the Storefront API returns the non-returnable collection, which needs that collection
   published on the renderer token's sales channel; this asks the enrichment app proxy instead. On the
   PLP of a non-returnable collection itself every card is badged without asking. */

import { isNonReturnable } from '@theme/hm-non-returnable';

const BADGE_CLASS = 'bottomBadge bottomBadge--non-returnable';
const OWN_BADGE = '[data-hm-plp-non-returnable]';

class HmPlpNonReturnable extends HTMLElement {
  /** @type {MutationObserver | undefined} */
  #observer;
  #scheduled = false;
  /** Product ids already sent to the proxy: re-renders must not ask again, even after a failure. */
  #asked = new Set();
  /** Product ids the proxy confirmed: later redraws of these cards are badged without waiting. */
  #confirmed = new Set();

  connectedCallback() {
    const root = this.closest('collection-section') ?? this.parentElement;
    if (!root || !this.dataset.label) return;

    // The renderer adds and re-renders cards after load (pagination, filters, badge refresh).
    this.#observer = new MutationObserver(() => this.#schedule(root));
    this.#observer.observe(root, { childList: true, subtree: true });
    this.#badgeCards(root);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = undefined;
  }

  /** @param {Element} root */
  #schedule(root) {
    if (this.#scheduled) return;
    this.#scheduled = true;
    requestAnimationFrame(() => {
      this.#scheduled = false;
      this.#badgeCards(root);
    });
  }

  /** @param {Element} root */
  #badgeCards(root) {
    const { endpoint, collectionIds, allCards } = this.dataset;

    root.querySelectorAll('product-card[data-br-product-id]').forEach((card) => {
      if (!(card instanceof HTMLElement)) return;
      // The renderer drew its own badge after ours (collection published on its channel): keep only one.
      if (card.querySelector(`.bottomBadge--non-returnable:not(${OWN_BADGE})`)) {
        card.querySelectorAll(OWN_BADGE).forEach((badge) => badge.remove());
        return;
      }
      const productId = card.dataset.brProductId;
      if (!productId || this.#hasBadge(card)) return;

      if (allCards === 'true' || this.#confirmed.has(productId)) {
        this.#addBadge(card);
        return;
      }
      if (!endpoint || !collectionIds || this.#asked.has(productId)) return;

      this.#asked.add(productId);
      isNonReturnable(endpoint, collectionIds, productId).then((nonReturnable) => {
        if (!nonReturnable) return;
        this.#confirmed.add(productId);
        // The card may have been redrawn meanwhile: badge every card currently showing this product.
        this.#badgeCards(root);
      });
    });
  }

  /**
   * The renderer's own badge (collection published on its channel) or ours from an earlier pass.
   * @param {HTMLElement} card
   */
  #hasBadge(card) {
    return Boolean(card.querySelector('.product-card__meta .bottomBadge--non-returnable'));
  }

  /** @param {HTMLElement} card */
  #addBadge(card) {
    const price = card.querySelector('.product-card__meta .product-card__price');
    if (!price) return;

    const badge = document.createElement('div');
    badge.className = BADGE_CLASS;
    // Not data-enriched-badge: the renderer removes those when it redraws its own badges.
    badge.dataset.hmPlpNonReturnable = 'true';
    badge.textContent = this.dataset.label ?? '';
    price.after(badge);
  }
}

if (!customElements.get('hm-plp-non-returnable')) {
  customElements.define('hm-plp-non-returnable', HmPlpNonReturnable);
}
